import React from 'react';
import styled from 'styled-components';
import { openHref } from '@zocdoc-frontend-common/utils-react';
import handlePropValidation from './utils/handlePropValidation';
import { BEFORE_TIMEOUT, timeout, throwErrOrOpenHref } from './utils/helpers';

/**
 * BaseLink
 * This component should only be used internally. It contains minimal styling. All functionality belongs here.
 * Link Variants should import this component and apply its own styling on top of it. The API should remain the exact same.
 *
 * @param {LinkProps} props - props for link
 * @returns {JSX.Element} BaseLink
 */
function BaseLink({
    href,
    className,
    children,
    onClick,
    beforeClick,
    tabIndex,
    target,
    rel,
    title,
    'data-test': dataTest,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    'aria-describedby': ariaDescribedby,
}) {
    const linkTagProps = {
        'aria-label': ariaLabel,
        'data-test': dataTest,
        'aria-labelledby': ariaLabelledby,
        'aria-describedby': ariaDescribedby,
        href,
        className,
        target,
        rel,
        title,
        tabIndex,
    };

    const buttonTagProps = {
        'aria-label': ariaLabel,
        'data-test': dataTest,
        'aria-labelledby': ariaLabelledby,
        'aria-describedby': ariaDescribedby,
        onClick,
        className,
        tabIndex,
    };

    function handleClick(event) {
        event.preventDefault();
        const beforePromise = new Promise((resolve, reject) => {
            try {
                resolve(beforeClick());
            } catch (err) {
                reject(err);
            }
        });

        return timeout(beforePromise, BEFORE_TIMEOUT)
            .then(() => openHref(event, href))
            .catch((err) => throwErrOrOpenHref(event, href, err));
    }

    handlePropValidation({ href, target, rel, onClick });

    if (typeof onClick === 'function') {
        return (
            <LinkStyledButton {...buttonTagProps}>{children}</LinkStyledButton>
        );
    } else {
        return (
            <a {...linkTagProps} onClick={handleClick}>
                {children}
            </a>
        );
    }
}

// We want to use a <button> tag instead of an <a> tag when there
// is an onClick prop for accessibility purposes
// However, for compatibility with older designs, it will be styled to look like a link
const LinkStyledButton = styled.button`
    background-color: transparent;
    padding: 0;
    font-size: inherit;
    font-family: inherit;
    border: none;

    &:hover {
        cursor: pointer;
    }
`;

export default BaseLink;
